import { ft } from '../core/units.js';
import { DAY_NAMES, parseTime } from './clock.js';
import { weekdayOf } from './npc.js';
import { floorOf } from './standing.js';
import { estimateMinutes, normalMinutes } from './beoGen.js';
import { CONSEQUENCES, OUTCOMES } from './noticed.js';
import {
  ACTS,
  FINALE,
  HEARD,
  HEARD_LINE,
  HELP,
  HOLDS_BACK,
  LOBBY_CLOCK,
  LOSS,
  OUTCOME,
  OWES,
  SLIPS,
  TOUR,
} from '../data/story.js';

/**
 * The story: which act you are in, and the man from the ownership group.
 *
 * It decides very little, and what it decides is when. Everything the acts are
 * made of already existed: chapters, standing, the board, perception, the
 * after-action record. This holds the order, the one visitor's hours, and the
 * sums at the end, and it hands the rest back to the modules that do the work:
 *
 *   the acts       advance on the exits in `data/story.js`, one at a time,
 *                  never backwards
 *   the visitor    is on the property only while this says he is touring,
 *                  and is posted to his meeting at four and to the ballroom on
 *                  the finale day. He perceives like anybody through the ordinary
 *                  perception module, and his reaction shape files nothing
 *   his file       is read, when it is asked for, from the write-up record and
 *                  the after-action record. Nothing is stored for it; a sloppy
 *                  week simply leaves more to read
 *   what he lets   slip is chosen from real records with the witness on them:
 *                  something one person on the crew saw and nobody else did
 *   the finale     is a card on one day's board. How each department helps is
 *                  its rung, spent through the release every shortfall uses
 *   the ending     is a guess and a choice, and the save goes on after both
 *
 * Nothing here names a person. The one who feeds him is the crew row that says
 * `feeds`; the visitor is the row that says `collects`; the six who help with
 * the finale are the gate rows that say `finale`.
 */

/** Minutes as the 24 hour text a BEO's times are written in. */
function clockText(minutes) {
  const wrapped = ((Math.round(minutes) % 1440) + 1440) % 1440;
  return `${String(Math.floor(wrapped / 60)).padStart(2, '0')}:${String(wrapped % 60).padStart(2, '0')}`;
}

export function createStory({
  standing,
  noticed,
  writeups,
  npcs = [],
  roster = [],
  shiftFor,
  /** The rung a department reads you on, card included. For the finale. */
  rungOf = () => 'cold',
  /** Whether a beat has been played, for the first act's exit. */
  played = () => false,
}) {
  let state = fresh();

  function fresh() {
    return {
      act: 1,
      began: { 1: null },
      arrived: null,
      stopped: null,
      finale: null,
      result: null,
      trial: null,
      due: null,
      guess: null,
      ending: null,
      gone: [],
      owes: 0,
      slips: [],
      /** The day he last said something to you in passing. */
      passed: null,
      /** The act's departments that have been warm since it began: won over. */
      won: {},
    };
  }

  /** The crew member who talks to him, and the visitor himself. By their rows. */
  const feeder = () => roster.find((person) => person.crew && person.feeds) ?? null;
  const helpers = () => roster.filter((person) => person.gate && person.finale);
  const isVisitor = (data) => Boolean(data?.visitor);

  const warm = (department) => standing.get(department) >= floorOf('warm');
  /*
   * Won over: warm now, or warm at any moment since the act began. An act
   * about three departments that do not like each other asked for all three
   * to be warm at the same moment, and a room that warmed one cooled another
   * (Banquets' share of every room is the Kitchen's loss): played normally,
   * the second act never ended (`fullgame.mjs`). Each one is won once.
   */
  const won = (department) => warm(department) || Boolean(state.won?.[department]);

  function exitMet(act) {
    const ends = ACTS.find((row) => row.n === act)?.ends;
    if (!ends) return false;
    if (ends.played && !played(ends.played)) return false;
    if (ends.warm && !ends.warm.every(won)) return false;
    if (ends.flag && !has(ends.flag)) return false;
    return true;
  }

  function has(flag) {
    switch (flag) {
      case 'clock-stopped':
        return state.stopped !== null;
      case 'reported':
        return state.ending === 'report';
      case 'covered':
        return state.ending === 'cover';
      case 'ended':
        return state.ending !== null;
      default:
        return false;
    }
  }

  /** Whether he tours on this day at all. */
  function touring(day) {
    if (state.arrived === null || day <= state.arrived || state.ending) return false;
    if (state.finale?.day === day) return true;
    return TOUR.days.includes(weekdayOf(day));
  }

  /** Where his four o'clock is today, or null for nowhere anybody would see. */
  function meetingOn(day) {
    if (!touring(day) || state.finale?.day === day) return null;
    return TOUR.meets[weekdayOf(day)] ?? null;
  }

  function within(minutes, [from, to]) {
    return minutes >= parseTime(from) && minutes < parseTime(to);
  }

  /** The shift's own hours, for the finale day, which he spends in the room. */
  function shiftHours(day) {
    const shift = shiftFor?.(day);
    if (!shift) return [TOUR.from, TOUR.until];
    return [shift.start, shift.end];
  }

  /**
   * Whether somebody the story governs is on the property this minute.
   *
   * Asked by each person, every frame, on top of their own hours. True for
   * everybody the story has no opinion about.
   */
  function onProperty(data, clock) {
    if (state.gone.includes(data.id)) return false;
    if (!isVisitor(data)) return true;
    const day = clock.day;
    if (!touring(day)) return false;
    if (state.finale?.day === day) return within(clock.minutes, shiftHours(day));
    if (within(clock.minutes, [TOUR.from, TOUR.until])) return true;
    return Boolean(meetingOn(day)) && within(clock.minutes, TOUR.meeting);
  }

  /** Where he should be standing instead of walking his tour, or null. */
  function standsAt(day, minutes) {
    if (state.finale?.day === day) return TOUR.watches;
    const meeting = meetingOn(day);
    // From the moment his tour ends, so he is posted while nobody can see him.
    if (meeting && minutes >= parseTime(TOUR.until) && minutes < parseTime(TOUR.meeting[1])) return meeting.at;
    return null;
  }

  /** What each visitor is posted to, so a change is only made once. */
  const posted = new Map();

  function place(clock) {
    for (const npc of npcs) {
      if (!isVisitor(npc.data)) continue;
      const at = standsAt(clock.day, clock.minutes);
      const key = at ? at.join(',') : null;
      if ((posted.get(npc.data.id) ?? null) === key) continue;
      posted.set(npc.data.id, key);
      /*
       * Put there rather than walked there. Every change here happens while
       * nobody can see him: the tour ends at half three and the meeting starts
       * at ten to four, and the finale is posted overnight. A reload in the
       * middle of a meeting is the one other way here, and he should be at it.
       */
      if (at) npc.post([{ x: ft(at[0]), z: ft(at[1]), dwell: 1e6 }], { from: true });
      else npc.unpost({ home: true });
    }
  }

  /**
   * Grant's file on you, as points, and where they came from.
   *
   * Read from what the building already keeps: the card as it stands, the
   * suspensions served, and every row of the after-action record. Not stored,
   * so there is nothing to keep in step: a claim that came to nothing is still
   * a claim somebody had, and a sloppy week leaves more of them.
   */
  function evidence() {
    const weights = OUTCOME.evidence;
    const visitors = new Set(npcs.filter((npc) => isVisitor(npc.data)).map((npc) => npc.data.id));
    const parts = { card: writeups.count, served: writeups.served, costly: 0, stirred: 0, collected: 0 };
    for (const entry of noticed.entries) {
      if (entry.subject) continue;
      if (visitors.has(entry.by)) parts.collected += 1;
      else if (CONSEQUENCES.includes(entry.outcome)) parts.costly += 1;
      else if (entry.stirred) parts.stirred += 1;
    }
    const points = Object.entries(parts).reduce((sum, [key, count]) => sum + count * weights[key], 0);
    return { parts, points };
  }

  /** How Management stands between him and your card, by the step you are on. */
  function cover() {
    const curtis = helpers().find((person) => person.finale.covers);
    if (!curtis) return { department: null, rung: null, times: 1 };
    const rung = rungOf(curtis.department);
    return { department: curtis.department, rung, times: curtis.finale.covers[rung] ?? 1 };
  }

  /**
   * Something one person on the crew saw and nobody else did, and he knows.
   *
   * Real records, with the witness on them: the after-action record keeps who
   * noticed what, and this is the rows where the one who talks to him is the
   * only one who noticed it. Something they never passed on to anybody in the
   * building, so there is no way he heard it in the building.
   */
  function slipFor(day) {
    const who = feeder();
    if (!who || state.ending || state.result) return null;
    if (state.slips.length >= SLIPS.most) return null;
    const last = state.slips[state.slips.length - 1];
    if (last) {
      let tours = 0;
      for (let d = last.day + 1; d <= day; d += 1) if (touring(d)) tours += 1;
      if (tours < SLIPS.every) return null;
    }
    const entries = noticed.entries;
    const told = new Set(state.slips.map((slip) => slip.id));
    const quiet = [OUTCOMES.LET_GO, OUTCOMES.OPEN];
    const candidates = entries.filter(
      (entry) =>
        entry.by === who.id &&
        !entry.subject &&
        quiet.includes(entry.outcome) &&
        HEARD[entry.what] &&
        !told.has(entry.id) &&
        !entries.some(
          (other) =>
            other.by !== who.id &&
            other.what === entry.what &&
            other.day === entry.day &&
            Math.abs((other.opened ?? 0) - (entry.opened ?? 0)) <= 15
        )
    );
    if (!candidates.length) return null;
    const pick = candidates.sort((a, b) => b.day - a.day || b.at - a.at)[0];
    const when =
      pick.day === day ? 'today' : pick.day === day - 1 ? 'yesterday' : `on ${DAY_NAMES[weekdayOf(pick.day)]}`;
    return { id: pick.id, line: HEARD_LINE.replace('{thing}', HEARD[pick.what]).replace('{when}', when) };
  }

  /** What he is saying to you in this conversation, chosen once when it starts. */
  let talk = null;

  return {
    get act() {
      return state.act;
    },
    get actName() {
      return ACTS.find((row) => row.n === state.act)?.name ?? '';
    },
    /**
     * The act a day's board is posted in: the one that had begun before that
     * day started. Pure in the day once the day has begun, like the rungs the
     * board reads, so an act changing at three in the afternoon does not
     * reshuffle the cards on the wall.
     */
    actOn(day) {
      let act = 1;
      for (const [n, began] of Object.entries(state.began)) {
        if (began !== null && began < day && Number(n) > act) act = Number(n);
      }
      return act;
    },
    /** Whether a department has been won over in this act (warm now, or since it began). */
    won: (department) => won(department),
    /** The departments an act is about, for the board to post more of their rooms. */
    departmentsOf(act) {
      return ACTS.find((row) => row.n === act)?.departments ?? [];
    },
    /** A copy of everything, for a check. */
    get state() {
      return JSON.parse(JSON.stringify(state));
    },
    has,
    touring,
    meetingOn,
    onProperty,
    evidence,
    cover,
    slipFor,
    get feeder() {
      return feeder();
    },
    /** A trial on: the agency has the big rooms. */
    trialOn: (day) => state.trial !== null && day < state.trial,
    get due() {
      return state.due;
    },
    get result() {
      return state.result ? { ...state.result } : null;
    },

    /**
     * One frame: the acts, his arrival, the clock, and a finale that went by.
     *
     * Returns what happened, for whoever wants to say so. Most frames nothing.
     */
    update(clock) {
      const out = [];
      const { day, minutes } = clock;
      if (state.began[1] === null) state.began[1] = day;
      state.won ??= {};
      for (const department of ACTS.find((row) => row.n === state.act)?.departments ?? []) {
        if (warm(department)) state.won[department] = true;
      }
      if (state.act < ACTS.length && exitMet(state.act)) {
        state.act += 1;
        state.began[state.act] = day;
        state.won = {};
        out.push({ kind: 'act', act: state.act });
      }
      if (state.act === 3 && state.arrived === null) {
        const act = ACTS.find((row) => row.n === 3);
        const either = act.departments.some(warm);
        if (either || day - state.began[3] >= TOUR.arrivesAfter) {
          state.arrived = day;
          out.push({ kind: 'arrived', day });
        }
      }
      if (
        state.act === 3 &&
        state.stopped === null &&
        meetingOn(day) &&
        minutes >= parseTime(LOBBY_CLOCK.stops) &&
        won('management')
      ) {
        state.stopped = { day, at: parseTime(LOBBY_CLOCK.stops) };
        out.push({ kind: 'clock', day });
      }
      if (state.finale && !state.result && day > state.finale.day) out.push({ kind: 'missed' });
      place(clock);
      return out;
    },

    /** A beat asked for something the story owns. */
    post(id, day) {
      if (id !== 'finale' || state.finale) return false;
      state.finale = { day: day + 1 };
      return true;
    },

    /** The finale, as a card, on its day. Pure in the day and the saved state. */
    finaleFor(day) {
      if (state.finale?.day !== day) return null;
      const shift = shiftFor?.(day);
      const start = parseTime(shift?.start ?? '14:00');
      let end = parseTime(shift?.end ?? '22:00');
      if (end <= start) end += 24 * 60;
      const estimate = estimateMinutes({ ...FINALE, shortfalls: FINALE.shortfalls }, { rungFor: rungOf });
      // Due when a normal player could set it (`normalMinutes`), and never so
      // late that its doors open after the shift: at least `FINALE.lead`.
      const lead = Math.max(FINALE.lead, Math.min(normalMinutes(estimate), end - start - FINALE.doorsAfter));
      const beo = {
        ...FINALE,
        day,
        setup: { ...FINALE.setup, layout: { ...FINALE.setup.layout } },
        shortfalls: FINALE.shortfalls.map((entry) => ({ amount: 0, ...entry, resolved: false, via: null })),
        postedAt: clockText(start),
        setupBy: clockText(start + lead),
        doors: clockText(start + lead + FINALE.doorsAfter),
        ghosts: false,
        finale: true,
      };
      delete beo.lead;
      delete beo.doorsAfter;
      beo.estimate = estimateMinutes(beo, { rungFor: rungOf });
      return beo;
    },

    /**
     * What each department does for the finale, by its rung, applied to the
     * accepted event's own shortfall list.
     *
     * `release` is the one every covered shortfall goes through, so staged
     * things land where that rung always lands things. Returns a line per
     * department, for the panel that says who did what.
     */
    prepare(beo, { release = () => {} } = {}) {
      const said = [];
      const list = beo.shortfalls;
      let cleared = false;
      for (const person of helpers()) {
        const rung = rungOf(person.department);
        const help = HELP[rung] ?? 'nothing';
        const row = person.finale;
        const entry = row.shortfall ? list.find((candidate) => candidate.id === row.shortfall) : null;
        if (help === 'stages' && entry && !entry.resolved) {
          entry.resolved = true;
          entry.via = 'finale';
          release(entry);
        }
        if (help === 'stages' && row.clears) cleared = true;
        if (help === 'obstructs') {
          if (entry && entry.amount) entry.amount += HOLDS_BACK;
          if (row.blocks && !list.some((candidate) => candidate.id === row.blocks)) {
            list.push({ id: row.blocks, amount: 0, resolved: false, via: null });
          }
        }
        const words = row.says?.[help === 'stages' ? 'helps' : help === 'obstructs' ? 'hinders' : 'nothing'];
        said.push({ id: person.id, name: person.name, department: person.department, rung, help, says: words });
      }
      if (cleared) {
        for (const entry of list) {
          if (entry.resolved || entry.amount) continue;
          if (!['lift-locked', 'dock-blocked'].includes(entry.id)) continue;
          entry.resolved = true;
          entry.via = 'finale';
        }
      }
      return said;
    },

    /**
     * The finale is over, one way or the other. Weighs it and says what it came
     * to: the letter, the departments behind you, and his file.
     */
    settle({ letter = null, missed = false } = {}) {
      if (!state.finale || state.result) return state.result;
      const grade = missed ? OUTCOME.grade.missed : OUTCOME.grade[letter] ?? OUTCOME.grade.F;
      const behind = {};
      let backing = 0;
      for (const department of standing.ids) {
        if (department === 'banquets') continue;
        const rung = rungOf(department);
        behind[department] = rung;
        backing += OUTCOME.behind[rung] ?? 0;
      }
      const file = evidence();
      const covered = cover();
      const pressure = (file.points * covered.times) / OUTCOME.perPoint;
      const points = Math.round((grade + backing - pressure) * 100) / 100;
      state.result = {
        letter,
        missed,
        grade,
        backing,
        behind,
        evidence: file,
        cover: covered.times,
        pressure: Math.round(pressure * 100) / 100,
        points,
        won: !missed && points >= OUTCOME.winAt,
        day: state.finale.day,
      };
      state.due = 'outcome';
      return { ...state.result };
    },

    /** The loss has a length. Set by whoever applies it, from its own day. */
    beginTrial(day) {
      state.trial = day + LOSS.days;
    },

    /** Where the last act has got to, one step at a time. */
    advance(next) {
      state.due = next;
    },

    /** Your answer to who fed him. */
    guess(id) {
      const who = feeder();
      state.guess = { id, right: Boolean(who && id === who.id) };
      return { ...state.guess };
    },
    get guessed() {
      return state.guess ? { ...state.guess } : null;
    },

    /** The last choice. The save goes on after it either way. */
    end(choice) {
      if (state.ending) return state.ending;
      state.ending = choice;
      state.due = null;
      const who = feeder();
      if (choice === 'report' && who) state.gone.push(who.id);
      if (choice === 'cover') state.owes = OWES;
      return choice;
    },

    /**
     * Whether the one you covered for lets this one go, and spends a turn of
     * what he owes you if he does.
     */
    forgives(id) {
      const who = feeder();
      if (state.ending !== 'cover' || !who || id !== who.id || state.owes <= 0) return false;
      state.owes -= 1;
      return true;
    },

    /**
     * What somebody the story is about says when you walk up to them, or null
     * for whatever they would have said anyway. Chosen when the conversation
     * starts (`met`) and held, because the box asks every frame.
     */
    speaks(npc) {
      if (talk?.npc === npc) return talk.line;
      if (npc.data.feeds && state.ending === 'cover') return npc.data.says?.owes ?? null;
      return null;
    },
    met(npc, day) {
      talk = null;
      if (!isVisitor(npc.data)) return;
      const slip = slipFor(day);
      if (slip) {
        state.slips.push({ day, id: slip.id });
        talk = { npc, line: slip.line };
        return;
      }
      const greets = npc.data.greets ?? [];
      talk = { npc, line: greets.length ? greets[day % greets.length] : npc.data.line ?? null };
    },

    /**
     * What he says walking past you, once a day: what he would have said had
     * you stopped him (the same slip, under the same rules, or his greeting),
     * over his head like anybody's words. Null if he has said his piece today.
     * A player who never walks up to the man in the suit still hears it, and
     * nothing on the screen says it means anything.
     */
    passing(npc, day) {
      if (!isVisitor(npc.data) || state.passed === day) return null;
      state.passed = day;
      const slip = slipFor(day);
      if (slip) {
        state.slips.push({ day, id: slip.id });
        return slip.line;
      }
      const greets = npc.data.greets ?? [];
      return greets.length ? greets[day % greets.length] : null;
    },

    toJSON: () => JSON.parse(JSON.stringify(state)),
    load(saved) {
      state = { ...fresh(), ...(saved ? JSON.parse(JSON.stringify(saved)) : {}) };
      posted.clear();
      talk = null;
    },
    clear() {
      state = fresh();
      posted.clear();
      talk = null;
    },
  };
}
